import vatgroupService from '../../../services/vatgroupService';
import filterService from '../../../services/filterService';

import {
    setVatGroupLoading,
    setVatGroupError,
    setVatGroupList,
    setVatGroupCurrent,
    setVatGroupPagination,
    setVatGroupSort,
    setVatGroupFilters,
    setVatGroupDefaultFilter,
    setVatGroupSettings,
    clearVatGroupCurrent,
    clearVatGroupError,
    setVatGroupFilterLoading,
    changeVatGroupFilterCondition,
    addVatGroupFilterCondition,
    updateVatGroupFilterConditionRule,
    updateVatGroupFilterConditionValue,
    removeVatGroupFilterCondition,
    resetVatGroupFiltersAndPagination
} from './vatgroupSlice';

export const fetchSettings = () => async (dispatch, getState) => {
    try {
        // Only fetch settings if they haven't been initialized
        if (!getState().vatgroup.isSettingsInitialized) {
            dispatch(setVatGroupLoading(true));
            const response = await vatgroupService.getSettings();

            // Set the settings
            dispatch(setVatGroupSettings(response));

            const activeFilter = response.filters.find(filter => filter.mark_default === true)
            if (activeFilter) {
                dispatch(setVatGroupDefaultFilter(activeFilter));
                return activeFilter;
            }
        }
        return {};
    } catch (error) {
        console.error('Error fetching settings:', error);
        dispatch(setVatGroupError({
            message: error.message || 'Failed to fetch settings',
            errors: error.errors || {}
        }));
        return {};
    } finally {
        dispatch(setVatGroupLoading(false));
    }
};

export const fetchVatgroups = (params = {}, filter = {}, shouldUseFilter) => async (dispatch, getState) => {
    try {
        // Check if settings are initialized

        const state = getState().vatgroup;
        if (!state.isSettingsInitialized) {
            shouldUseFilter ? filter = await dispatch(fetchSettings()) : await dispatch(fetchSettings());
        }

        const finalQueryParams = {
            ...state.filters,
            page: state.pagination.current_page,
            per_page: state.pagination.per_page,
            sort_field: state.sort.field,
            sort_direction: state.sort.direction,
            ...params
        };

        const finalActiveFilter = shouldUseFilter ? (Object.keys(filter).length === 0 ? state.defaultFilter : { ...state.defaultFilter, ...filter }) : {};

        dispatch(setVatGroupLoading(true));
        const response = await vatgroupService.getList(finalQueryParams, finalActiveFilter);
        dispatch(setVatGroupList(response));
        return response;
    } catch (error) {
        console.error('Error in fetchVatgroups:', error);
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {},
            status: error.status || 500
        };
        dispatch(setVatGroupError(serializedError));
        throw serializedError;
    } finally {
        dispatch(setVatGroupLoading(false));
    }
};

export const fetchVatGroup = (id) => async (dispatch) => {
    try {
        dispatch(setVatGroupLoading(true));
        const response = await vatgroupService.getOne(id);
        dispatch(setVatGroupCurrent(response.data));
    } catch (error) {
        dispatch(setVatGroupError({
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        }));
    } finally {
        dispatch(setVatGroupLoading(false));
    }
};

export const createVatGroup = (data) => async (dispatch) => {
    try {
        const response = await vatgroupService.create(data);
        return response.data || response;
    } catch (error) {
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        };
        throw serializedError;
    }
};

export const updateVatGroup = (id, data) => async (dispatch) => {
    try {
        const response = await vatgroupService.update(id, data);
        return response.data || response;
    } catch (error) {
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        };
        throw serializedError;
    }
};

export const deleteVatGroup = (id) => async (dispatch) => {
    try {
        dispatch(setVatGroupLoading(true));
        const response = await vatgroupService.delete(id);
        return response;
    } catch (error) {
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        };
        dispatch(setVatGroupError(serializedError));
        throw serializedError;
    } finally {
        dispatch(setVatGroupLoading(false));
    }
};

export const setPagination = (pagination) => (dispatch) => {
    dispatch(setVatGroupPagination(pagination));
};

export const setSort = (sort) => (dispatch) => {
    dispatch(setVatGroupSort(sort));
};

export const searchVatgroups = (query) => (dispatch, getState) => {
    const currentFilters = getState().vatgroup.filters;
    dispatch(setVatGroupFilters({ ...currentFilters, search: query }));
};

// New filter-related actions
export const setDefaultFilter = (data) => {
    return async (dispatch) => {
        try {
            await dispatch(setVatGroupDefaultFilter(data));
        } catch (error) {
            console.error('Error setting default filter:', error);
        }
    };
};

export const changeCondition = (condition) => {
    return (dispatch) => {
        dispatch(changeVatGroupFilterCondition(condition));
    };
};

export const addNewCondition = (rule) => {
    return (dispatch) => {
        dispatch(addVatGroupFilterCondition(rule));
    };
};

export const updateOptionRule = (index, data) => {
    return (dispatch) => {
        dispatch(updateVatGroupFilterConditionRule({ index, data }));
    };
};

export const updateOptionValue = (index, value) => {
    return (dispatch) => {
        dispatch(updateVatGroupFilterConditionValue({ index, value }));
    };
};

export const removeCondition = (index) => {
    return (dispatch) => {
        dispatch(removeVatGroupFilterCondition(index));
    };
};

export const saveFilterCondition = (filterData) => async (dispatch, getState) => {
    try {
        dispatch(setVatGroupFilterLoading(true));
        const response = await filterService.createFilterCondition(filterData);
        if (!response?.errors) {
            const currentSettings = getState().vatgroup.settings;
            dispatch(setVatGroupSettings({ ...currentSettings, filters: response.filters }));
        }
        dispatch(setVatGroupFilterLoading(false));
        return response;
    } catch (error) {
        console.error('Error saving filter:', error);
        dispatch(setVatGroupFilterLoading(false));
        dispatch(setVatGroupError({
            message: error.message || 'Failed to save filter',
            errors: error.errors || {}
        }));
        throw error;
    }
};

export const resetFiltersAndPagination = () => (dispatch) => {
    dispatch(resetVatGroupFiltersAndPagination());
};